import React, { useState } from "react";
import CrossAttachmentAssessment from "./CrossAttachmentAssessment";
import ZoomableImage from "../zoomablePic";
import { iconData } from "../../iconData";
import { IP } from "../../internalServer";
import { Box, Paper, Typography, Chip } from "@mui/material";
import { CheckCircle2 } from "lucide-react";
import PdfViewer from "./PdfViewer";

const ACCENT = "#f77f00";
const ACCENT_SOFT = "#fff4e5";
const BORDER = "#e6e9ef";
const TEXT_PRIMARY = "#1f2733";
const TEXT_SECONDARY = "#5b6472";
const BG_SOFT = "#f8f9fb";

const McqDisplayMcq = ({ nextQuestion, question }) => {
  const [mideaviewer, setMideaviewer] = useState(false);
  const [responseAttach, setResponseAttach] = useState("");

  const handleClickforpdf = (fileUrl) => {
    setTimeout(() => {
      setResponseAttach(fileUrl);
      setMideaviewer(true);
    }, 100);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        borderRadius: "14px",
        border: `1px solid ${BORDER}`,
        background: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: ACCENT,
          boxShadow: "0 4px 16px rgba(247,127,0,0.08)",
        },
      }}
    >
      {/* Header row: Q badge + title + marks */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${ACCENT} 0%, #d35400 100%)`,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "13px",
              fontWeight: 700,
              flexShrink: 0,
              boxShadow: "0 3px 8px rgba(247,127,0,0.3)",
            }}
          >
            Q{nextQuestion + 1}
          </Box>

          <Typography
            sx={{
              fontSize: "14.5px",
              fontWeight: 600,
              color: TEXT_PRIMARY,
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {question.title?.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                {index !== 0 && <br />}
                <span>{line}</span>
              </React.Fragment>
            ))}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {question?.correct_score ? (
            <Chip
              label={`${question.correct_score} Marks`}
              size="small"
              sx={{
                background: ACCENT_SOFT,
                color: ACCENT,
                fontWeight: 700,
                fontSize: "11.5px",
                border: `1px solid ${ACCENT}`,
              }}
            />
          ) : null}
          {question?.is_mandatory && (
            <Chip
              label="* Mandatory"
              size="small"
              sx={{
                background: "#fff0f0",
                color: "#dc3545",
                fontWeight: 600,
                fontSize: "10.5px",
                border: "1px solid #f5c2c7",
              }}
            />
          )}
        </Box>
      </Box>

      {/* Attachments */}
      {question?.attachments?.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {question.attachments.map((attachment, index) => {
            const fileName = attachment || "";
            const fileExtension = fileName.split(".").pop()?.toLowerCase();
            const isImage =
              fileExtension === "jpg" ||
              fileExtension === "jpeg" ||
              fileExtension === "png";

            return (
              <div key={index} style={{ maxWidth: "200px" }} className="w-auto">
                {isImage ? (
                  <ZoomableImage
                    src={attachment}
                    alt={`Attachment ${index + 1}`}
                  />
                ) : (
                  <CrossAttachmentAssessment attachment={attachment} />
                )}
              </div>
            );
          })}
        </Box>
      )}

      {/* Options */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {question?.options?.map((option, index) => {
          const isCorrect = question?.correct_answers?.some(
            (ans) => ans?.option_key === option?.key,
          );

          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 1.25,
                px: 1.5,
                borderRadius: "10px",
                border: `1px solid ${isCorrect ? "#22c55e" : BORDER}`,
                background: isCorrect ? "#e8f7ee" : BG_SOFT,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "8px",
                  background: isCorrect ? "#22c55e" : "#fff",
                  border: `1.5px solid ${isCorrect ? "#22c55e" : BORDER}`,
                  color: isCorrect ? "#fff" : TEXT_SECONDARY,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "12px",
                  flexShrink: 0,
                }}
              >
                {option?.key}
              </Box>

              <Typography
                sx={{
                  fontSize: "13px",
                  color: TEXT_PRIMARY,
                  flex: 1,
                  lineHeight: 1.5,
                }}
              >
                {option?.value}
              </Typography>

              {isCorrect && <CheckCircle2 size={16} color="#22c55e" />}
            </Paper>
          );
        })}
      </Box>

      {mideaviewer && (
        <PdfViewer
          setMideaviewer={setMideaviewer}
          attachment={responseAttach}
        />
      )}
    </Paper>
  );
};

export default McqDisplayMcq;
